import React, { useCallback, useEffect, useRef, useState } from "react";
import { Avatar, Box, Button, CircularProgress, IconButton, Stack, TextField, Tooltip, Typography } from "@mui/material";
import EditIcon from "@mui/icons-material/EditOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import SaveIcon from "@mui/icons-material/CheckCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import { toast } from "react-toastify";
import { del, getData, patchData, postData } from "../../../apis/api";
import { ENDPOINTS } from "../../../apis/urls";

const API = ENDPOINTS.CALLS;
const initials = (name) => (name || "?").trim().split(/\s+/).slice(0, 2).map((part) => part[0]).join("").toUpperCase();
const when = (value) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
};

/**
 * Notes an agent writes against a call, live during a pickup or long after it.
 * Autosaves nothing: an explicit Save keeps a half-typed thought out of the
 * record, and the draft is kept per call so a closing popup does not lose it.
 */
export default function CallNotes({ callId, handoverId, autoFocus = false, emptyHint }) {
  const [notes, setNotes] = useState(null);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editDraft, setEditDraft] = useState("");
  const alive = useRef(true);
  const drafts = useRef({});

  const load = useCallback(async () => {
    if (!callId) return;
    try {
      const data = await getData(API.NOTES(callId));
      if (alive.current) setNotes(data.Content || []);
    } catch (_) {
      if (alive.current) setNotes([]);
    }
  }, [callId]);

  useEffect(() => {
    alive.current = true;
    setNotes(null);
    setDraft(drafts.current[callId] || "");
    load();
    return () => { alive.current = false; };
  }, [callId, load]);

  const save = async () => {
    const body = draft.trim();
    if (!body || saving) return;
    setSaving(true);
    try {
      const data = await postData(API.NOTES(callId), { body, handoverId });
      setNotes((rows) => [data.Content, ...(rows || [])]);
      setDraft("");
      delete drafts.current[callId];
    } catch (error) {
      toast.error(typeof error?.Content === "string" ? error.Content : "Could not save the note. It is still in the box — try again.");
    } finally {
      setSaving(false);
    }
  };

  const commitEdit = async (note) => {
    const body = editDraft.trim();
    if (!body) return;
    try {
      const data = await patchData(`${API.NOTES(callId)}/${note.id}`, { body });
      setNotes((rows) => rows.map((row) => (row.id === note.id ? data.Content : row)));
      setEditing(null);
    } catch (error) {
      toast.error(typeof error?.Content === "string" ? error.Content : "Could not update the note.");
    }
  };

  const remove = async (note) => {
    try {
      await del(`${API.NOTES(callId)}/${note.id}`);
      setNotes((rows) => rows.filter((row) => row.id !== note.id));
    } catch (error) {
      toast.error(typeof error?.Content === "string" ? error.Content : "Could not delete the note.");
    }
  };

  return <Stack spacing={1.5}>
    <Box>
      <TextField
        multiline minRows={2} maxRows={6} fullWidth size="small" autoFocus={autoFocus}
        placeholder={emptyHint || "What did the caller need? Anything to follow up on?"}
        value={draft}
        onChange={(event) => { setDraft(event.target.value); drafts.current[callId] = event.target.value; }}
        onKeyDown={(event) => { if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) save(); }}
      />
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 0.75 }}>
        <Typography variant="caption" color="text.secondary">Ctrl/⌘ + Enter to save</Typography>
        <Button size="small" variant="contained" disableElevation onClick={save} disabled={!draft.trim() || saving}
                startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}>
          {saving ? "Saving" : "Save note"}
        </Button>
      </Stack>
    </Box>

    {notes === null && <Stack direction="row" spacing={1} alignItems="center"><CircularProgress size={14} /><Typography variant="caption" color="text.secondary">Loading notes…</Typography></Stack>}
    {notes?.length === 0 && <Typography variant="caption" color="text.secondary">No notes on this call yet.</Typography>}

    {(notes || []).map((note) => <Stack key={note.id} direction="row" spacing={1.25} alignItems="flex-start"
      sx={{ p: 1.25, borderRadius: 1.5, bgcolor: "action.hover" }}>
      <Avatar sx={{ width: 26, height: 26, fontSize: 11 }}>{initials(note.authorName)}</Avatar>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" spacing={0.75} alignItems="baseline" sx={{ flexWrap: "wrap" }}>
          <Typography variant="caption" sx={{ fontWeight: 600 }}>{note.authorName}</Typography>
          <Typography variant="caption" color="text.secondary">{when(note.created_at)}{note.edited ? " · edited" : ""}</Typography>
        </Stack>
        {editing === note.id
          ? <Stack spacing={0.5} sx={{ mt: 0.5 }}>
              <TextField multiline minRows={2} fullWidth size="small" value={editDraft} onChange={(event) => setEditDraft(event.target.value)} />
              <Stack direction="row" spacing={0.5}>
                <Button size="small" startIcon={<SaveIcon fontSize="small" />} onClick={() => commitEdit(note)}>Save</Button>
                <Button size="small" color="inherit" startIcon={<CloseIcon fontSize="small" />} onClick={() => setEditing(null)}>Cancel</Button>
              </Stack>
            </Stack>
          : <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{note.body}</Typography>}
      </Box>
      {note.mine && editing !== note.id && <Stack direction="row">
        <Tooltip title="Edit note"><IconButton size="small" aria-label={`Edit note ${note.id}`} onClick={() => { setEditing(note.id); setEditDraft(note.body); }}><EditIcon fontSize="small" /></IconButton></Tooltip>
        <Tooltip title="Delete note"><IconButton size="small" aria-label={`Delete note ${note.id}`} onClick={() => remove(note)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
      </Stack>}
    </Stack>)}
  </Stack>;
}
